import { create } from 'zustand'
import type { SupportResponse, HistoryEntry, AnalyticsEvent } from '../types'
import { submitQuery as apiSubmitQuery } from '../lib/api'
import { addHistoryEntry, addAnalyticsEvent } from '../lib/api-db'

interface QueryState {
  query: string
  isLoading: boolean
  response: SupportResponse | null
  error: string | null
  responseTime: number
  currentHistoryId: string | null

  setQuery: (query: string) => void
  submitQuery: () => Promise<void>
  clearResponse: () => void
  loadFromHistory: (entry: HistoryEntry) => void
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

export const useQueryStore = create<QueryState>((set, get) => ({
  query: '',
  isLoading: false,
  response: null,
  error: null,
  responseTime: 0,
  currentHistoryId: null,

  setQuery: (query) => set({ query }),

  submitQuery: async () => {
    const { query } = get()

    if (!query.trim() || query.length < 10) {
      set({ error: 'Please enter a question (minimum 10 characters)' })
      return
    }

    set({ isLoading: true, error: null, response: null })

    const startTime = performance.now()

    try {
      const response = await apiSubmitQuery(query)
      const responseTime = Math.round(performance.now() - startTime)

      // Create history entry
      const historyId = generateId()
      const historyEntry: HistoryEntry = {
        id: historyId,
        query,
        response,
        timestamp: new Date(),
        responseTime,
        copiedPlatforms: [],
      }

      // Save to database
      await addHistoryEntry(historyEntry)

      // Track analytics event
      const analyticsEvent: AnalyticsEvent = {
        id: generateId(),
        type: 'query',
        confidence: response.confidence,
        responseTime,
        timestamp: new Date(),
      }
      await addAnalyticsEvent(analyticsEvent)

      set({
        response,
        responseTime,
        isLoading: false,
        currentHistoryId: historyId,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred'
      set({
        error: message,
        isLoading: false,
      })
    }
  },

  clearResponse: () =>
    set({
      query: '',
      response: null,
      error: null,
      responseTime: 0,
      currentHistoryId: null,
    }),

  loadFromHistory: (entry) =>
    set({
      query: entry.query,
      response: entry.response,
      responseTime: entry.responseTime,
      currentHistoryId: entry.id,
      error: null,
    }),
}))
